import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from 'src/services/auth.service';
import { UserProfile } from 'src/services/auth/userProfile';
import { JwtTokenService } from 'src/services/jwt-token.service';
import { LocalStorage } from 'src/utils/local-storage';
import { DatalayerService } from 'src/services/datalayer.service';
import { TokenTransferService } from 'src/services/token-transfer-service';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  public loginErrorMessage$: BehaviorSubject<string>;

  public loginForm: FormGroup;
  public isButtonDisabled = false;
  public userIp$: Observable<string>;

  public userProfile: UserProfile = null;
  isMobile = false;

  constructor(private fb: FormBuilder,
    private renderer: Renderer2,
    private authService: AuthService,
    private localStorage: LocalStorage,
    private router: Router,
    private jwtTokenService: JwtTokenService,
    private tokenTransferService: TokenTransferService,
    private datalayer: DatalayerService) {
    this.validate();
  }

  ngOnInit(): void {    
    this.checkIfMobile();
    window.addEventListener('resize', () => this.checkIfMobile());
    
    this.userIp$ = this.authService.getIpCheckMessage();
    this.loginErrorMessage$ = new BehaviorSubject<string>('');
    this.loginPageViewEvent();

    const params = new Proxy(new URLSearchParams(window.location.search),
      { get: (searchParams, prop) => searchParams.get(prop as string), }
    );
    const pParam = (params as any).p;

    if (pParam !== null) {
      this.authService.getAutoLoginByParam(pParam).subscribe({
        next: token => {
          // Successful login
          this.redirectSuccessfulLogin(token);
        },
        error: err => {
          this.loginErrorEvent(err, 'autologin');
        }
      });
    }

    if (typeof params.get === 'function') { 

      const userid = params.get('userid');
      const password = params.get('password');

      if (userid && password) {
        // Populate the Angular login form
        this.loginForm.patchValue({
          username: userid,
          password: password
        });

          // Auto-submit
          setTimeout(() => this.login(), 200);
      }
    }
  }

  validate(): void {
    this.loginForm = this.fb.group({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rememberMe: [false]     
    });
  }

login(): void {
  this.loginForm.markAllAsTouched();
  this.isButtonDisabled = true;

  if (this.loginForm.invalid) {
    this.isButtonDisabled = false;
    return;
  }

  const username = this.loginForm.controls.username.value;
  const password = this.loginForm.controls.password.value;

  this.authService.loginRequest(username, password, 'hub')
    .subscribe({
      next: token => {
        // Successful login
        this.redirectSuccessfulLogin(token);
      },
      error: err => {
        this.loginErrorEvent(err, username);
      }
    });
  }
  
  tabUser(): void {
    this.renderer.selectRootElement('#password').focus();
  }

  get loginFormControls() {
    return this.loginForm.controls;
  }

  redirectSuccessfulLogin(token: any) {
    this.userProfile = new UserProfile();
              
    this.jwtTokenService.setToken(token.access_token);
    this.userProfile = this.jwtTokenService.getUserInfo();
    this.userProfile.access_token = token.access_token;
    this.userProfile.refresh_token = token.refresh_token;
    const updatedAuthorities = this.jwtTokenService.getAuthorities();
    this.userProfile.authorities = updatedAuthorities;

    this.localStorage.setAccessToken(token.access_token);
    this.localStorage.setRefreshToken(token.refresh_token);
    this.localStorage.setUserProfile(this.userProfile);
    this.datalayer.loginSuccess();

    this.router.navigate(['home']);
  }

  loginErrorEvent(err: any, username: string) {
    // Failed login
    this.isButtonDisabled = false;
    this.loginErrorMessage$?.next(err.error.error_description);

    // Push login failure event to dataLayer
    this.datalayer.loginFailure(username);
  }

  private loginPageViewEvent(): void {
    this.datalayer.loginPageView();
  }

  checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
}
